import React, { useState, useRef, useEffect } from "react";

import { Tooltip, MenuItem } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiEye, FiPlusCircle } from "react-icons/fi";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import { StyledMenu } from "../../../../../../components/Generic/AnchorMenu";
import ConfirmChangeStatus from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Error } from "../../../../../../models/error";
import { Service as ServiceModel } from "../../../../../../models/service";
import { Searches, Filters, Sorts, Sort, Row } from "../../../../../../models/shipeezi.table";
import Service from "../../../../../../services/service.booking.service";
import { Status } from "../../../../../../util/enums";
import { getMeasurement } from "../../../../../../util/getMeasurement";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import { formatCurrency } from "../../../../../../util/prices";
import PriceGroupPage, { PriceGroupPageHandles } from "../PriceGroupPage";
import PriceGroupMaintenance, { PriceGroupMaintenanceHandles } from "../PriceGroupPage/Maintenance";
import ServiceComponentModal from "./ServiceModal";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
    changeStatus: boolean,
  }
}

const ServicePage = (props: Props) => {
  const {permission} = props;
  const classes = useStyles();
  const { currentUser } = useAppState();

  const measurement = getMeasurement();

  const { t: translate } = useTranslation();

  const [serviceBooks, setServiceBooks] = useState<ServiceModel[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceModel>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isViewMode, setIsViewMode] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>();

  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  const { refetch, isFetching } = useQuery(
    ["service.list", page, pageSize, filters, searches, sorting],
    () =>
      Service.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "groups" }]),
        sortBy: sorting
          ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
          : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setServiceBooks(response.data);
      },
    }
  );

  const { refetch: refetchServiceTotal } = useQuery(
    ["service.total", filters, searches],
    () =>
      Service.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateService, isLoading: isUpdating } = useMutation(
    ["service.booking.update"],
    ({ id, service }: { id: number; service: ServiceModel }) => Service.update(id, service),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.service",
        });
        setHasError(false);
        refetch();
        refetchServiceTotal();
      },
      onError: (error: AxiosError<Error>) => {
        setHasError(true);
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const { mutate: createService, isLoading: isCreating } = useMutation(
    ["service.create"],
    (service: ServiceModel) => Service.create(service),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasError(false);
        refetch();
        refetchServiceTotal();
      },
      onError: (error: AxiosError<Error>) => {
        setHasError(true);
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const editService = (index: number) => {
    const findServiceBook = serviceBooks.find((service) => Number(service.id) === Number(index));

    if (findServiceBook) {
      if (hasError !== undefined) setHasError(undefined);
      setSelectedService(findServiceBook);
      setIsViewMode(false);
      setOpenModal(true);
    }
  };

  const viewService = (index: number) => {
    const findServiceBook = serviceBooks.find((service) => Number(service.id) === Number(index));

    return (
      <Tooltip title={translate("view") || ""} placement="top">
        <div>
          <FiEye
            size={18}
            className={classes.button}
            color="#475467"
            onClick={() => {
              if (findServiceBook) {
                if (hasError !== undefined) setHasError(undefined);
                setIsViewMode(true);
                setSelectedService(findServiceBook);
                setOpenModal(true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const changeStatusService = (index: number) => {
    const findServiceBook = serviceBooks.find((service) => Number(service.id) === Number(index));

    if (findServiceBook) {
      updateService({
        id: Number(index),
        service: {
          ...findServiceBook,
          status:
            findServiceBook.status && findServiceBook.status === Status.ACTIVE
              ? Status.INACTIVE
              : Status.ACTIVE,
        },
      });
    }
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveServiceSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, serviceBooks);

    setRows([...rowsSelected]);
  };

  const formatMeasurementFeeTitle = (columnTitle: string) => {
    if (measurement && measurement.code) {
      const code = measurement.code?.toUpperCase();

      return columnTitle.replace("{dimension}", code);
    }

    return columnTitle.replace("{dimension} ", "");
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    {
      name: "sellType",
      searchIdentifier: "sell_type",
      title: translate("type"),
    },
    {
      name: "sellDescription",
      title: translate("description"),
      searchIdentifier: "sell_description",
      formatComponent: (value?: string) => value,
    },
    {
      name: "sellM3Fee",
      title: formatMeasurementFeeTitle(translate("DimensionFee")),
      hideSearch: true,
      formatText: (price: number) =>
        `${currentUser?.company?.currency?.symbol ?? "$"} ${formatCurrency(
          price,
          currentUser?.company?.currency,
          false
        )}`,
    },
    {
      name: "sellFlatFee",
      title: translate("flatFee"),
      hideSearch: true,
      formatText: (price: number) =>
        `${currentUser?.company?.currency?.symbol ?? "$"} ${formatCurrency(
          price,
          currentUser?.company?.currency,
          false
        )}`,
    },
    {
      name: "sellHourlyFee",
      title: translate("hourlyFee"),
      hideSearch: true,
      formatText: (price: number) =>
        `${currentUser?.company?.currency?.symbol ?? "$"} ${formatCurrency(
          price,
          currentUser?.company?.currency,
          false
        )}`,
    },
    {
      name: "sellHalfHourFee",
      title: translate("halfHourFee"),
      hideSearch: true,
      formatText: (price: number) =>
        `${currentUser?.company?.currency?.symbol ?? "$"} ${formatCurrency(
          price,
          currentUser?.company?.currency,
          false
        )}`,
    },
    {
      name: "status",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (status: string) => status && badgeStatus(status),
    },
  ];

  const handleNewService = () => {
    if (hasError !== undefined) setHasError(undefined);
    setSelectedService(undefined);
    setIsViewMode(false);
    setOpenModal(true);
    setAnchorEl(null);
  };

  const priceGroupModalRef = useRef<PriceGroupMaintenanceHandles>(null);
  const handleNewGroup = () => {
    priceGroupModalRef?.current?.openModal();
  };

  const priceGroupRef = useRef<PriceGroupPageHandles>(null);
  const handleViewGroups = () => (
    <Button
      onClick={() => {
        priceGroupRef.current?.openScreen();
      }}
      customColor={Color.INFO}
    >
      {translate("viewGroups")}
    </Button>
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleNewServiceOrGroup = () => {
    const open: boolean = !!anchorEl;

    return (
      <div>
        <Tooltip title={translate("createANewServiceOrGroup")} placement="top">
          <div>
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              customColor={Color.PRIMARY}
              className={classnames(open && classes.createButtonActive)}
              startIcon={<FiPlusCircle size={18} />}
              data-cy="button-create-service-or-group"
            >
              {translate("create")}
            </Button>
          </div>
        </Tooltip>
        <StyledMenu
          className={classes.createContainer}
          id="extra-actions-menu"
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={() => setAnchorEl(null)}
          data-cy="menu-create-service-or-group"
        >
          <MenuItem
            className={classes.createItem}
            onClick={() => {
              setAnchorEl(null);
              handleNewService();
            }}
            data-cy="menu-option-service"
          >
            {translate("newService")}
          </MenuItem>
          <MenuItem
            className={classes.createItem}
            data-cy="menu-option-group-create"
            onClick={() => {
              setAnchorEl(null);
              handleNewGroup();
            }}
          >
            {translate("newGroup")}
          </MenuItem>
        </StyledMenu>
      </div>
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy", "handleViewGroups"],
      right: ["handleChangeStatus", "handleNewServiceOrGroup"],
    },
    handleViewGroups: handleViewGroups(),
    handleNewServiceOrGroup: permission && isShowAction(currentUser, permission.create)
      ? handleNewServiceOrGroup()
      : undefined,
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        { column: "code", name: translate("code") },
        {
          column: "sellType",
          name: translate("type"),
        },
        {
          column: "sellM3Fee",
          name: translate("m3Fee"),
        },
        {
          column: "sellFlatFee",
          name: translate("flatFee"),
        },
        {
          column: "sellHourlyFee",
          name: translate("hourlyFee"),
        },
        {
          column: "sellHalfHourFee",
          name: translate("halfHourFee"),
        },
        {
          column: "sellDescription",
          name: translate("description"),
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: permission && isShowAction(currentUser, permission.changeStatus)
      ? renderButtonToChangeStatus()
      : undefined,
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setHasError(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasError]);

  return (
    <div data-cy="serviceBookList">
      <ShipeeziNewGrid
        data={serviceBooks}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating || isCreating}
        emptyData={isFetching ? "Loading..." : translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (serviceId) => editService(Number(serviceId))
            : undefined,
          handleChange: permission && isShowAction(currentUser, permission.changeStatus)
            ? (serviceId) => changeStatusService(Number(serviceId))
            : undefined,
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewService }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveServiceSelected(rowsArr)}
        generalActions={generalActions}
      />

      {openModal && (
        <ServiceComponentModal
          serviceToUpdate={selectedService}
          hasError={hasError}
          open={openModal}
          isOnViewMode={isViewMode}
          isLoading={isUpdating || isCreating}
          closeModal={() => setOpenModal(false)}
          submit={(service: ServiceModel) => {
            if (selectedService && selectedService?.id) {
              updateService({
                id: selectedService.id,
                service: {
                  ...selectedService,
                  ...service,
                },
              });
            } else {
              createService({
                ...service,
                companyId: currentUser?.companyId,
              } as ServiceModel);
            }
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasError}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateService({ id: rowId, service: { status } as ServiceModel });
              setClearSelection(true);
            }
          }}
        />
      )}

      <PriceGroupMaintenance ref={priceGroupModalRef} />
      <PriceGroupPage ref={priceGroupRef} />
    </div>
  );
};

export default ServicePage;
